import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Link2, Loader2, Search } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import {
  associateLabResult,
  getLabMatchCandidates,
  type LabMatchCandidate,
  type LabResultSource,
} from '@/services/lab-orders.service';
import { searchPatients } from '@/services/patients.service';
import type { PatientSummary } from '@/types/patient.types';

/**
 * Legacy color tiers from quest-lab-details.php L852-863.
 * Score=1→lime-50, 2→green-50, 3→emerald-50, 4+→emerald-100.
 */
function matchBackground(score: number): string {
  if (score >= 4) return 'bg-emerald-100';
  if (score === 3) return 'bg-emerald-50';
  if (score === 2) return 'bg-green-50';
  if (score === 1) return 'bg-lime-50';
  return 'bg-slate-50';
}

function candidateName(c: LabMatchCandidate): string {
  const name = [c.lastName, c.firstName].filter(Boolean).join(', ').trim();
  return name || 'Unknown';
}

function summaryName(p: PatientSummary): string {
  const name = [p.lastName, p.firstName].filter(Boolean).join(', ').trim();
  return name || 'Unknown';
}

export interface LabMatchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  source: LabResultSource;
  labResultId: number;
  /** Name parsed off the lab result, shown for context. */
  resultPatientName?: string | null;
  onAssociated?: (patientId: number) => void;
}

export function LabMatchDialog({
  isOpen,
  onClose,
  source,
  labResultId,
  resultPatientName,
  onAssociated,
}: LabMatchDialogProps) {
  const queryClient = useQueryClient();

  const [candidates, setCandidates] = useState<LabMatchCandidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<PatientSummary[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // A failed search must not read as "No patients found" (SCRUM-2069).
  const [searchFailed, setSearchFailed] = useState(false);
  const [submittingId, setSubmittingId] = useState<number | null>(null);

  const searchDebounceRef = useRef<number | null>(null);

  // ─── Load scored candidates on open ──────────────────────────────────────

  useEffect(() => {
    if (!isOpen) {
      setCandidates([]);
      setSearchTerm('');
      setSearchResults([]);
      setSubmittingId(null);
      return;
    }

    setCandidatesLoading(true);
    getLabMatchCandidates(source, labResultId)
      .then((rows) => setCandidates(rows))
      .catch(() => setCandidates([]))
      .finally(() => setCandidatesLoading(false));
  }, [isOpen, source, labResultId]);

  // ─── Patient search (debounced) ──────────────────────────────────────────

  useEffect(() => {
    if (searchDebounceRef.current) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const term = searchTerm.trim();
    setSearchFailed(false);
    if (term.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    // A search for an older term can settle after a newer one; only the
    // latest may touch the results.
    let cancelled = false;
    setSearchLoading(true);
    searchDebounceRef.current = window.setTimeout(() => {
      searchPatients(term)
        .then((rows) => {
          if (cancelled) return;
          setSearchFailed(false);
          setSearchResults(rows);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error('LabMatchDialog: patient search failed', err);
          setSearchResults([]);
          setSearchFailed(true);
        })
        .finally(() => {
          if (!cancelled) setSearchLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      if (searchDebounceRef.current) {
        window.clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [searchTerm]);

  // ─── Associate ───────────────────────────────────────────────────────────

  const handleAssociate = useCallback(
    async (patientId: number, label: string) => {
      if (submittingId != null) return;

      setSubmittingId(patientId);
      try {
        await associateLabResult(source, labResultId, patientId);
        toast.success('Patient matched', `Lab result linked to ${label}.`);

        void queryClient.invalidateQueries({ queryKey: ['lab-orders'] });
        void queryClient.invalidateQueries({ queryKey: ['lab-result-detail', source, labResultId] });
        // Drop the Labs nav count too — prefix match, so it hits the key's
        // accountId/facilityId tail. Without this the bubble holds its old
        // number for up to the 60s staleTime and the queue looks stuck.
        void queryClient.invalidateQueries({ queryKey: ['navigation', 'badges'] });

        onAssociated?.(patientId);
        onClose();
      } catch (error) {
        toast.error('Match failed', getErrorMessage(error, 'Unable to link the lab result.'));
      } finally {
        setSubmittingId(null);
      }
    },
    [labResultId, onAssociated, onClose, queryClient, source, submittingId],
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Match Lab Result to Patient" size="xl">
      {resultPatientName && (
        <div className="mb-4 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
          <span className="font-medium text-slate-700">Result for:</span> {resultPatientName}
        </div>
      )}

      <div className="flex flex-col gap-5">
        <section>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">Possible Matches</h3>
          {candidatesLoading ? (
            <div className="flex items-center justify-center py-6">
              <Spinner size="sm" />
            </div>
          ) : candidates.length === 0 ? (
            <p className="rounded-md bg-slate-50 px-3 py-3 text-sm text-slate-500">No matches found.</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {candidates.map((c) => (
                <li key={c.patientId}>
                  <button
                    type="button"
                    onClick={() => void handleAssociate(c.patientId, candidateName(c))}
                    disabled={submittingId != null}
                    className={`group flex w-full flex-col gap-1 rounded-md border border-slate-200 px-3 py-2 text-left transition-colors hover:border-blue-400 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60 ${matchBackground(c.matchScore)}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-slate-900">
                        {candidateName(c)}{c.displayId ? ` (#${c.displayId})` : ''}
                      </span>
                      <span className="flex items-center gap-2 text-xs text-slate-500">
                        {submittingId === c.patientId && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        {c.matchScore > 0 && <span>match score {c.matchScore}</span>}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-600">
                      {c.email && (
                        <span className={c.matches.email ? 'font-semibold text-emerald-700' : ''}>
                          {c.matches.email && <Check className="mr-0.5 inline h-3 w-3" />}
                          {c.email}
                        </span>
                      )}
                      {c.cellPhone && (
                        <span className={c.matches.phone ? 'font-semibold text-emerald-700' : ''}>
                          {c.matches.phone && <Check className="mr-0.5 inline h-3 w-3" />}
                          {c.cellPhone}
                        </span>
                      )}
                      {c.birthDate && (
                        <span className={c.matches.dob ? 'font-semibold text-emerald-700' : ''}>
                          {c.matches.dob && <Check className="mr-0.5 inline h-3 w-3" />}
                          DOB {c.birthDate}
                        </span>
                      )}
                      {c.city && c.state && (
                        <span>{c.city}, {c.state}</span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">Search Patients</h3>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type at least 2 characters to search..."
              className="w-full rounded-md border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {searchTerm.trim().length >= 2 && (
            <div className="mt-2">
              {searchLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Spinner size="sm" />
                </div>
              ) : searchFailed ? (
                <p role="alert" className="rounded-md bg-red-50 px-3 py-3 text-sm text-red-700">
                  Patient search isn&apos;t working right now. Try again in a moment. If it keeps happening, contact
                  Prospera support.
                </p>
              ) : searchResults.length === 0 ? (
                <p className="rounded-md bg-slate-50 px-3 py-3 text-sm text-slate-500">No patients found.</p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {searchResults.map((p) => (
                    <li key={p.patientId}>
                      <button
                        type="button"
                        onClick={() => void handleAssociate(p.patientId, summaryName(p))}
                        disabled={submittingId != null}
                        className="group flex w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-left hover:border-blue-400 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <span className="flex flex-col gap-0.5">
                          <span className="text-sm font-semibold text-slate-900">
                            {summaryName(p)}{p.displayId ? ` (#${p.displayId})` : ''}
                          </span>
                          <span className="text-xs text-slate-500">
                            {[p.email, p.cellPhone || p.phone].filter(Boolean).join(' · ') || '—'}
                          </span>
                        </span>
                        {submittingId === p.patientId ? (
                          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                        ) : (
                          <Link2 className="h-4 w-4 text-slate-400 group-hover:text-blue-500" />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
}
